/**
 * 이상한 방 - 세션의 소리 문 하나 (소리 · 진동 · 배경음)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 11.3절(클라이언트가 해도 되는 것)
 *
 * `session.ts`에서 갈라져 나왔다. 리듀서는 "언제 울릴지"만 정하고(`Effect.playSound`)
 * 무엇이 어떻게 울리는지는 모른다. 그 마지막 한 칸을 잇는 것이 이 파일이고,
 * 규칙이 아니라 **번역**이므로 세션의 원칙을 깨지 않는다.
 *
 * ## 문이 하나인 것이 규칙이다
 *
 * 소리와 진동은 **같은 함수 하나**를 지난다(`play`). 진동에 별도의 호출 지점을 파면
 * "소리는 나는데 진동은 안 나는" 경로가 조용히 생기고, 그건 안드로이드에서만 드러난다.
 * 관측용 사본(`SoundSink`)도 같은 자리에서 나가므로 셋이 갈릴 창이 없다.
 *
 * ## 무엇을 고르는지는 여기서 정하지 않는다
 *
 * 리듀서의 `SoundId` → 사전의 `SoundName` 변환과 라운드 끝소리 선택은 **세션에 남아 있다.**
 * 끝소리는 단계 기준·발견 수를 알아야 고를 수 있고(`roundOverSound`), 그건 이 파일이
 * 들고 있으면 안 되는 것이다 - 여기 있는 것은 이름 하나를 받아 울리는 손 하나뿐이다.
 *
 * ## 실패를 사건으로 만들지 않는다
 *
 * `audio/`의 모든 실패 경로가 던지지 않고 `false`를 돌려준다는 계약을 그대로 잇는다.
 * 소리가 없는 것은 잔고장이고, 소리 때문에 사격이 멈추는 것은 판을 잃는 일이다.
 */

import { bgm } from '../audio/bgm'
import { audio } from '../audio/engine'
import { haptics, type HapticPattern } from '../audio/haptics'
import { sound, type SoundName } from '../audio/sounds'

/**
 * 소리 이름 → 진동 패턴. **새 이벤트를 만들지 않는다.**
 *
 * 진동은 소리와 **같은 문 하나**를 지난다(`playSound`). 리듀서가 효과를 내고, 세션이 그것을
 * 소리 이름으로 옮기고, 그 지점에서 진동도 함께 나간다. 진동에 별도의 호출 지점을 파면
 * "소리는 나는데 진동은 안 나는" 경로가 조용히 생기고, 그건 안드로이드에서만 드러난다.
 *
 * 표가 짧은 것이 규칙이다(`haptics.ts`: "정답·오답·중요 사건에만"). 여기 없는 소리는
 * 진동하지 않는다 - 워프·페이즈 전환·이미 찾은 곳까지 울리면 손이 라운드 내내 떨고,
 * 늘 떠는 손은 아무것도 알려 주지 않는다.
 *
 * `perfect`와 `roundClear`가 같은 패턴인 이유는 둘이 같은 사건이기 때문이다 - 판이 끝났고
 * 목표를 이뤘다. `roundFail`을 넣지 않은 것은 실패에 진동을 얹으면 그 판의 마지막 감각이
 * 벌점이 되어서다(빗맞은 한 발은 이미 `miss`가 말했다).
 */
export const HAPTIC_FOR_SOUND: Readonly<Partial<Record<SoundName, HapticPattern>>> = {
  miss: 'miss',
  reload: 'reload',
  lowAmmo: 'lowAmmo',
  perfect: 'allFound',
  roundClear: 'allFound',
  // 봉투(P5). 라운드 밖에서만 울리므로 수색 중에 손이 떠는 횟수는 늘지 않았고, 아래 등급(N·R)에는
  // 두 이름이 아예 오지 않는다 - 그 조용함이 SR·SSR의 값어치다(`ui/envelopeCeremony.ts` 머리말).
  envelopeImpact: 'envelopeImpact',
  envelopeBurst: 'envelopeLegend',
}

/**
 * 소리가 하나 났다는 **알림**이다. 재생기가 아니다.
 *
 * 재생은 언제나 `audio/engine.ts`의 엔진이 한다(음소거·해제·동시 발음 상한을 그것이 소유한다).
 * 여기로 넘어오는 것은 관측용 사본이며, 지금 쓰는 곳은 개발 훅 하나다 - 배경 탭·자동 검증에서는
 * 소리를 들을 수 없으므로 **"울렸는가"를 눈으로 확인할 경로**가 없으면 이 배선을 검증할 방법이 없다.
 *
 * @param name    사전의 소리 이름(`audio/sounds.ts`). 리듀서의 `SoundId`가 아니다 -
 *                라운드의 끝소리처럼 **세션이 골라야 하는 것**이 있어서 변환 뒤의 이름을 넘긴다.
 * @param comboStep 발견음의 반음 단계(첫 발견이 0). `hit` 말고는 무시된다.
 */
export type SoundSink = (name: SoundName, comboStep: number) => void

/**
 * 소리 하나를 낸다. **진동이 나가는 문은 이것 하나다** - 라운드 세션의 `play`도, 라운드 밖의 봉투 화면도
 * (`main.ts`) 여기를 지난다.
 *
 * 봉투가 제 자리에서 `haptics.vibrate(...)`를 부르는 형태를 고르지 않은 이유는 위 `HAPTIC_FOR_SOUND`의
 * 주석 그대로다 - 진동에 별도의 호출 지점을 파면 "소리는 나는데 진동은 안 나는" 경로가 조용히 생기고,
 * 그건 안드로이드에서만 드러난다. 문이 하나면 표를 고치는 것으로 두 화면이 함께 따라온다.
 *
 * 던지지 않는다. 미지원·끔·미해제·쿨다운은 전부 조용한 `false`다(`haptics.ts`의 계약).
 */
export function playWithHaptics(name: SoundName, comboStep = 0): void {
  audio.play(sound(name, comboStep))
  /*
   * 발견음만 따로 갈라지는 이유는 길이가 콤보에 따라 늘어나기 때문이고, 넘기는 `comboStep`은 소리가 쓰는
   * 값과 **같다** - 두 감각이 같은 단계를 말해야 "이 발견이 저만큼 쌓였다"가 하나로 읽힌다.
   */
  if (name === 'hit') {
    haptics.hitCombo(comboStep)
    return
  }
  const pattern = HAPTIC_FOR_SOUND[name]
  if (pattern !== undefined) haptics.vibrate(pattern)
}

export type SessionAudio = Readonly<{
  /** 소리 하나 + 같은 자리의 진동 + 관측 사본. 던지지 않는다. */
  play: (name: SoundName, comboStep?: number) => void
  /** 이 판의 곡을 뽑는다. **한 판에 한 곡이고 도중에 바뀌지 않는다.** */
  startBgm: () => void
  /**
   * 이 세션이 배경음을 이미 뽑았는가. **판당 한 번**이라는 규칙의 잠금이다.
   * 실패해서 곡이 없는 경우(로드 실패·컨텍스트 없음)에도 참이 된다 - 다시 뽑아 봐야
   * 같은 실패가 반복될 뿐이고, 그 재시도가 프레임마다 도는 경로에 들어가면 그게 사고다.
   */
  hasStartedBgm: () => boolean
  /** 세션이 접힐 때. 곡과 진동을 함께 끊는다. */
  stop: () => void
}>

/**
 * 세션 하나의 소리 문을 연다.
 *
 * @param notify 관측용 알림. 생략해도 소리는 그대로 난다 - 재생은 엔진의 몫이다(`SoundSink`).
 */
export function createSessionAudio(notify?: SoundSink | undefined): SessionAudio {
  const notifySound: SoundSink = notify ?? (() => undefined)

  let bgmStarted = false

  /**
   * 소리 하나를 낸다. **던지지 않는다** - 엔진은 음소거·미해제·컨텍스트 없음을 전부 조용한
   * `false`로 처리하지만, 사전 조회가 `undefined`를 돌려주는 경로만은 여기서 막아야 한다.
   * 소리 하나가 없는 것은 잔고장이고, 소리 때문에 사격이 멈추는 것은 판을 잃는 일이다.
   */
  function play(name: SoundName, comboStep = 0): void {
    // 소리와 진동은 같은 문 하나를 지난다(`playWithHaptics`). 여기서 더하는 것은 관측용 사본뿐이다.
    playWithHaptics(name, comboStep)
    notifySound(name, comboStep)
  }

  /**
   * 이 판의 배경음을 뽑는다. **한 판에 한 곡이고 도중에 바뀌지 않는다.**
   *
   * 외우기↔수색 전환에서 부르지 않는 것이 이 함수의 요점이다. 페이즈마다 곡을 바꾸는
   * 로테이션은 관찰 게임에서 해악이다 - 곡이 계속 바뀌면 배경으로 물러나지 않고, 물러나지
   * 않는 소리는 방을 보는 주의를 계속 자기 쪽으로 가져간다. 결과 화면까지 같은 곡으로 간다.
   *
   * 부르는 곳은 셋뿐이고 전부 **판이 새로 서는 순간**이다: 세션이 활성으로 태어날 때,
   * 외우기 페이즈에 새로 들어올 때(= 재도전·한 판 더·재시작), 잠들어 있던 세션이 깨어날 때.
   * 단계 이동은 무대를 통째로 갈아 끼우므로(`main.ts`의 `swapStage`) 옛 세션의 `dispose`가
   * 먼저 곡을 끄고 새 세션이 다시 뽑는다 - **두 곡이 겹칠 창이 없다.**
   * 겹치지 않는 근거가 순서만은 아니다: `bgm.start()`는 뽑기 전에 스스로 앞 곡을 끈다.
   */
  function startBgm(): void {
    bgmStarted = true
    bgm.start('play')
  }

  return {
    play,
    startBgm,
    hasStartedBgm: () => bgmStarted,

    stop(): void {
      /*
       * 곡을 여기서 끈다. 무대 교체는 **먼저 버리고 나중에 세우므로**(`main.ts`의 `swapStage`)
       * 이 한 줄이 다음 판의 첫 곡보다 언제나 먼저 실행된다 - 두 곡이 겹쳐 울릴 창이 없다.
       * 진동도 함께 끊는다. 라운드가 사라진 뒤에도 손이 떨고 있으면 그건 유령이다.
       */
      bgm.stop()
      /*
       * 해제 전에 부르는 것은 걱정하지 않아도 된다 - `haptics.cancel()`이 스스로 미해제를
       * 걸러 낸다(그 파일의 "해제 전에는 백엔드를 부르지 않는다"). 한때 여기 "울린 적이
       * 있는가" 깃발을 두어 같은 검사를 한 벌 더 했는데, 그건 크롬의 인터벤션 경고를 막으려던
       * 것이고 그 방어는 이제 제자리에 있다.
       */
      haptics.cancel()
    },
  }
}
